import { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react'
import { flushSync } from 'react-dom'
import { Button }   from '@/components/ui/button'
import { Input }    from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Label }    from '@/components/ui/label'
import {
  cn, toCssLength, toStyleObject, setHtmlLikeJQuery, isSelectTrue,
  coerceDate, coerceInt, coerceFloat, formatFrDate,
  dateToIsoInput, isoInputToDate, minutesToTimeInput, timeInputToMinutes,
  HAS_WEBKIT_DATE_ICON,
} from '@/lib/utils'
import { X, Eye, EyeOff, Calendar } from 'lucide-react'
import { createGeneralApi } from '../tbsGeneralApi'
import { useT } from '@/lib/i18n'

function testMask(value, mask) {
  if (!mask || value === '' || value == null) return true
  try { return new RegExp(`^(?:${mask})$`).test(String(value)) } catch { return true }
}

const fmtChampMontant = v => Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 2 })

// Coercition en entrée (JSON initial, setValue(), sync de prop `value`) selon le maskComp — cf.
// « Valeurs par type » (claude-sub-specifs/champs-combo.md) : Date (104) → objet Date JS ; Heure
// (106) → entier (minutes depuis minuit) ; Numerique/Montant (109/113) → flottant ; Checkbox (115)
// → booléen, toujours (pas d'état "absent" distinct de "décoché", contrairement aux autres types).
// Autres maskComp : valeur inchangée.
function coerceByMc(mc, v) {
  switch (mc) {
    case 104: return coerceDate(v)
    case 106: return coerceInt(v)
    case 109:
    case 113: return coerceFloat(v)
    case 115: return isSelectTrue(v)
    default:  return v
  }
}

// maskComp dont la valeur absente doit être sérialisée en chaîne vide (getValue()/getJson()),
// plutôt qu'en null — Bouton (101), HTML (107), Checkbox (115, cf. coerceByMc) et File (119) exclus.
const MC_EMPTY_AS_STRING = new Set([104, 106, 109, 111, 112, 113, 116, 120, 130, 131, 132])

// Sérialisation getJson()/FormData selon le maskComp — contrat "toujours une chaîne" (chaîne vide
// ou null selon MC_EMPTY_AS_STRING si absente, sauf Checkbox qui n'a pas d'état absent — cf. coerceByMc).
function champFieldToJsonStr(mc, v) {
  if (mc === 115) return v ? 'true' : 'false'
  if (v == null) return MC_EMPTY_AS_STRING.has(mc) ? '' : null
  if (mc === 104) return formatFrDate(v)
  return String(v)
}

export default function ChampField({ id, label, value, maskComp, aceLanguage, inputMask, mode, required, placeholder, visible, fixedHeight, multiple, onEvent, compoGroup, userData, class: cssClass, style: rawStyle }) {
  const t      = useT()
  const cssStyle = toStyleObject(rawStyle)
  const mc     = Number(maskComp)
  const [prevValue, setPrevValue] = useState(value)
  const [local, setLocal]         = useState(() => coerceByMc(mc, value))
  const [touched, setTouched]     = useState(false)
  const [error, setError]         = useState(false)
  const [fmtError, setFmtError]   = useState(null)
  const [showPwd, setShowPwd]     = useState(false)
  const [modeState, setModeState]               = useState(mode)
  const [requiredState, setRequiredState]       = useState(!!required)
  const [maskState, setMaskState]               = useState(inputMask)
  const [placeholderState, setPlaceholderState] = useState(placeholder)
  const [disabledState, setDisabledState]       = useState(false)
  const [tooltip, setTooltip]                   = useState(undefined)
  const isRead = modeState === 'read'
  // Hauteur fixe (scroll interne) — mc 107, 111, 112, 130, 131, 132
  const heightStyle = fixedHeight ? { height: toCssLength(fixedHeight), overflowY: 'auto' } : undefined
  // `visible: false` (JSON) → display:none posé directement sur le div [data-tbs-root], pas sur
  // un wrapper externe : show()/hide() (API générale, tbsGeneralApi.js) ciblent ce même élément.
  const visibilityStyle = visible === false ? { display: 'none' } : undefined

  const localRef           = useRef(local)
  const maskStateRef       = useRef(maskState ?? null)
  const editorContainerRef = useRef(null)  // div ACE / textarea CKEditor / div Quill
  const editorInstanceRef  = useRef(null)  // instance ACE / CKEditor / Quill
  const htmlContainerRef   = useRef(null)  // div HTML (mc=107)
  // true quand le changement de `local` vient de l'éditeur lui-même (saisie utilisateur) : évite
  // à l'effet de sync ci-dessous de rappeler setValue()/setData()/dangerouslyPasteHTML() sur
  // chaque frappe, ce qui replace le curseur en tête de document (setValue externe = seul cas
  // où l'effet de sync doit réellement pousser `local` dans l'éditeur).
  const skipNextSyncRef   = useRef(false)
  // Élément visuel du champ lui-même (bouton, input, textarea, div d'affichage lecture seule...) —
  // distinct du div [data-tbs-root] (wrapper englobant label + champ + message d'erreur) ciblé par
  // les méthodes générales de tbsGeneralApi.js. setWidth()/getWidth()/setHeight()/getHeight() sont
  // surchargées ci-dessous pour cibler cette ref plutôt que le wrapper — sans quoi elles ne
  // redimensionnent qu'une boîte invisible sans effet visuel sur le champ (le wrapper n'étire pas
  // ses enfants en hauteur, `flex-col` par défaut ; en largeur ça passe inaperçu car les champs
  // texte sont déjà `w-full`, mais un `<Button>`/case à cocher ne l'est pas).
  const fieldElRef         = useRef(null)
  const checkboxBtnRef     = useRef(null)  // bouton Radix Checkbox (mc=115)
  const checkboxDomRef     = useRef(null)  // <input type="checkbox"> invisible (mc=115), porte l'id canonique
  const compoGroupRef      = useRef(compoGroup ?? null)
  const userDataRef        = useRef(userData ?? null)
  const aceLanguageRef     = useRef(aceLanguage ?? null)
  const onEventRef         = useRef(onEvent)
  const isReadRef          = useRef(isRead)
  const requiredStateRef   = useRef(requiredState)
  const disabledRef        = useRef(false)
  useLayoutEffect(() => { localRef.current = local; onEventRef.current = onEvent; maskStateRef.current = maskState; isReadRef.current = isRead; requiredStateRef.current = requiredState; disabledRef.current = disabledState })


  useEffect(() => {
    window.tbsComponents = window.tbsComponents ?? {}
    const generalApi = createGeneralApi(id, userDataRef.current)
    window.tbsComponents[id] = {
      type: 'champ',
      maskComp: mc,
      ...generalApi,
      // Surcharge de setWidth()/getWidth()/setHeight()/getHeight() (générique dans tbsGeneralApi.js,
      // qui cible [data-tbs-root] — le wrapper englobant label + champ + message d'erreur) pour agir
      // sur le champ lui-même (fieldElRef) plutôt que sur ce wrapper : un `flex-col` n'étire pas ses
      // enfants en hauteur, donc redimensionner le wrapper seul n'a aucun effet visuel sur le champ.
      // CKEditor (mc=131) gère sa propre mise en page (DOM injecté hors du contrôle React) : passe
      // par son API interne resize() plutôt que par un style CSS direct. Quill (mc=132) expose la
      // zone éditable via `editor.root`, distincte du conteneur qui accueille aussi sa toolbar.
      setWidth: width => {
        if (mc === 131 && editorInstanceRef.current) {
          const h = editorInstanceRef.current.container?.getSize?.('height')
          editorInstanceRef.current.resize(toCssLength(width), h)
          return
        }
        const el = fieldElRef.current
        if (el) el.style.width = toCssLength(width)
      },
      getWidth: () => {
        if (mc === 131 && editorInstanceRef.current?.container) return editorInstanceRef.current.container.getSize('width')
        return fieldElRef.current ? fieldElRef.current.offsetWidth : undefined
      },
      setHeight: height => {
        if (mc === 131 && editorInstanceRef.current) {
          const w = editorInstanceRef.current.container?.getSize?.('width')
          editorInstanceRef.current.resize(w, toCssLength(height))
          return
        }
        if (mc === 132 && editorInstanceRef.current?.root) {
          editorInstanceRef.current.root.style.height = toCssLength(height)
          editorInstanceRef.current.root.style.overflowY = 'auto'
          return
        }
        const el = fieldElRef.current
        if (el) el.style.height = toCssLength(height)
      },
      getHeight: () => {
        if (mc === 131 && editorInstanceRef.current?.container) return editorInstanceRef.current.container.getSize('height')
        if (mc === 132 && editorInstanceRef.current?.root) return editorInstanceRef.current.root.offsetHeight
        return fieldElRef.current ? fieldElRef.current.offsetHeight : undefined
      },
      // `getValue()`/`getJson()` — même sérialisation chaîne pour les deux (compatibilité code JS
      // legacy de l'appli cible) : Date (104) → "JJ/MM/AAAA" ; Checkbox (115) → "true"/"false",
      // toujours ; Heure (106)/Numerique/Montant (109/113) → chaîne. `local`/`localRef.current`
      // reste en interne un type natif coercé (coerceByMc : objet Date, entier, flottant, booléen)
      // — nécessaire au rendu (`<input type="date"/"time"/"number">`, `checked`) — seule la valeur
      // *exposée* par ces deux méthodes est sérialisée en chaîne.
      getValue:      () => champFieldToJsonStr(mc, localRef.current),
      // `flushSync` : le champ porte l'input natif soumis par `tbsGlobalForm.submit()`
      // (`HTMLFormElement.submit()`, sans event `submit`) — sans flush synchrone, un `submit()`
      // appelé juste après ce `setValue()` (même traitement métier) lirait encore l'ancienne
      // valeur du DOM, le commit React n'ayant pas encore eu lieu.
      setValue:      (v, fireEvent) => { const cv = coerceByMc(mc, v); flushSync(() => setLocal(cv)); localRef.current = cv; setError(false); if (fireEvent) onEventRef.current(id, 'onChange', cv) },
      getJson:       () => champFieldToJsonStr(mc, localRef.current),
      getCompoGroup: () => compoGroupRef.current,
      setCompoGroup: v => { compoGroupRef.current = v },
      setReadOnly:    v => { const b = !!v; setModeState(b ? 'read' : 'write'); isReadRef.current = b },
      isReadOnly:     () => isReadRef.current,
      setMandatory:   () => { setRequiredState(true);  requiredStateRef.current = true },
      unsetMandatory: () => { setRequiredState(false); requiredStateRef.current = false },
      isRequired:     () => requiredStateRef.current,
      setRequired:    v  => { const b = !!v; setRequiredState(b); requiredStateRef.current = b },
      setMask:        inputMaskValue => { maskStateRef.current = inputMaskValue; setMaskState(inputMaskValue) },
      setPlaceholder: placeholderText => setPlaceholderState(placeholderText),
      setTooltip:     tooltipText => setTooltip(tooltipText),
      setDisabled: v => {
        const b = !!v; setDisabledState(b); disabledRef.current = b
        if (mc === 130) editorInstanceRef.current?.setReadOnly(b)
        if (mc === 131) { try { editorInstanceRef.current?.setReadOnly(b) } catch { /* instance pas encore prête */ } }
        if (mc === 132) { try { b ? editorInstanceRef.current?.disable() : editorInstanceRef.current?.enable() } catch { /* instance pas encore prête */ } }
      },
      isDisabled: () => disabledRef.current,
      setFocus: () => {
        if (mc === 130 || mc === 131 || mc === 132) editorInstanceRef.current?.focus?.()
        else if (mc === 115) checkboxBtnRef.current?.focus()
        else document.getElementById(id)?.focus()
      },
      ...(mc === 115 && {
        setChecked: (v, fireEvent) => { const cv = !!v; localRef.current = cv; flushSync(() => setLocal(cv)); setError(false); if (fireEvent) onEventRef.current(id, 'onChange', cv) },
        isChecked:  () => !!localRef.current,
      }),
      // Surcharge de addCss()/getCss()/removeCss()/addClassCss()/removeClassCss() (génériques dans
      // tbsGeneralApi.js, qui ciblent [data-tbs-root]) pour agir sur le `<Button>` lui-même
      // (fieldElRef) — même raison que la surcharge setWidth()/setHeight() ci-dessus, et cohérent
      // avec `class`/`style` (JSON), posés directement sur ce même bouton (voir plus bas dans le
      // rendu) : sans cette surcharge, l'API impérative et l'attribut JSON cibleraient deux éléments
      // DOM différents pour le même composant, avec des effets visuels différents.
      ...(mc === 101 && {
        addCss: (...args) => {
          const el = fieldElRef.current
          if (!el) return
          if (args.length === 1 && args[0] !== null && typeof args[0] === 'object') {
            for (const [k, v] of Object.entries(args[0])) el.style.setProperty(k, v)
          } else {
            for (let i = 0; i < args.length - 1; i += 2) el.style.setProperty(args[i], args[i + 1])
          }
        },
        getCss: cssStyleName => {
          const el = fieldElRef.current
          return el ? getComputedStyle(el).getPropertyValue(cssStyleName) : undefined
        },
        removeCss: (...cssNames) => {
          const el = fieldElRef.current
          if (!el) return
          for (const name of cssNames) el.style.removeProperty(name)
        },
        addClassCss: cssClassName => {
          const el = fieldElRef.current
          if (!el) return
          for (const cls of String(cssClassName).split(' ').filter(Boolean)) el.classList.add(cls)
        },
        removeClassCss: cssClassName => {
          const el = fieldElRef.current
          if (!el) return
          for (const cls of String(cssClassName).split(' ').filter(Boolean)) el.classList.remove(cls)
        },
      }),
      ...(mc === 130 && {
        // Surcharge show()/setVisible(true) — container passant de display:none à visible sans
        // `editor.resize()` synchrone, ACE garde ses métriques internes (largeur/hauteur/lignes
        // visibles) calculées à 0 (mesurées au montage ou au dernier hide()). Un `setValue()` appelé
        // juste après reste alors invisible (parfois carrément vide à l'écran) tant que le
        // ResizeObserver (asynchrone, un frame plus tard) n'a pas déclenché son propre resize() —
        // d'où le comportement non systématique. `editor.resize(true)` ici force une lecture
        // synchrone des dimensions (reflow navigateur immédiat) avant tout setValue() ultérieur.
        show: () => { generalApi.show(); editorInstanceRef.current?.resize(true) },
        setVisible: v => { generalApi.setVisible(v); if (v) editorInstanceRef.current?.resize(true) },
        getAceLanguage: () => aceLanguageRef.current,
        setAceLanguage: lang => {
          aceLanguageRef.current = lang
          editorInstanceRef.current?.session?.setMode(`ace/mode/${lang}`)
        },
        // annotations : [{ row, column, text, type }, ...] — type : 'error'/'warning'/'information'.
        // ACE n'a pas de type 'information' en interne (seulement 'info') : le renderer de la
        // marge plante (undefined.replace) sur un type inconnu — normalisé ici avant transmission.
        setAnnotations: annotations => editorInstanceRef.current?.getSession()?.setAnnotations(
          (annotations ?? []).map(a => (a?.type === 'information' ? { ...a, type: 'info' } : a))
        ),
        moveCursorToPosition: position    => editorInstanceRef.current?.moveCursorToPosition(position),
        selectLine:           ()          => editorInstanceRef.current?.selection?.selectLine(),
        scrollToRow:          lineNo      => editorInstanceRef.current?.scrollToLine(lineNo, true, true, () => {}),
      }),
      validate: () => {
        const v = localRef.current
        const hasVal = v !== '' && v != null
        if (!isReadRef.current && hasVal) {
          // inputMask check (toujours actif si mask défini et valeur présente)
          if (maskStateRef.current && !testMask(v, maskStateRef.current)) {
            setFmtError(t('invalidFormat')); setError(false); return false
          }
          // Format natif pour mc 104/106/109/113 — `v` déjà typé (coerceByMc), une valeur non
          // parsable produit un Invalid Date / NaN plutôt qu'une exception (cf. coerceDate/coerceInt/
          // coerceFloat), détecté ici plutôt que par un test de format sur une représentation chaîne.
          if (mc === 104 || mc === 106 || mc === 109 || mc === 113) {
            const fmtOk = mc === 104 ? (v instanceof Date && !isNaN(v.getTime())) : (typeof v === 'number' && !isNaN(v))
            if (!fmtOk) { setFmtError(t('invalidFormat')); setError(false); return false }
          }
        }
        setFmtError(null)
        if (!requiredStateRef.current || isReadRef.current || mc === 101 || mc === 107 || mc === 119) { setError(false); return true }
        const isEmpty = mc === 115 ? !localRef.current : (localRef.current === '' || localRef.current == null)
        setError(isEmpty)
        return !isEmpty
      },
    }
    return () => { delete window.tbsComponents[id] }
  }, [id, mc, isRead, requiredState])

  // ACE — initialisation
  useEffect(() => {
    if (mc !== 130 || isRead || !editorContainerRef.current) return
    if (!window.ace) { console.warn('[ChampField] window.ace non défini'); return }
    let active = true
    const editor = window.ace.edit(editorContainerRef.current)
    // Trait vertical par défaut d'ACE à la colonne 80 ("print margin", hérité d'un usage IDE — sans
    // pertinence ici, aucun attribut JSON pour le piloter) — masqué systématiquement.
    editor.setShowPrintMargin(false)
    if (aceLanguageRef.current) editor.session.setMode(`ace/mode/${aceLanguageRef.current}`)
    editor.setValue(localRef.current ?? '', -1)
    editor.on('change', () => {
      if (!active) return
      const v = editor.getValue()
      skipNextSyncRef.current = true
      localRef.current = v
      flushSync(() => setLocal(v))
      onEventRef.current(id, 'onChange', v)
    })
    editorInstanceRef.current = editor
    // ACE ne redessine pas automatiquement au redimensionnement de son conteneur (poignée `resize:
    // vertical` posée sur ce dernier, cf. rendu ci-dessous) — sans ce ResizeObserver, le canvas interne
    // reste figé à la taille de montage tant qu'aucun autre événement (frappe, scroll) ne force un recalcul.
    const ro = new ResizeObserver(() => editor.resize())
    ro.observe(editorContainerRef.current)
    return () => { active = false; ro.disconnect(); editor.destroy(); editorInstanceRef.current = null }
  }, [mc, isRead]) // eslint-disable-line

  // CKEditor — initialisation
  // Guard `active` indispensable en StrictMode : destroy() est synchrone mais instanceReady
  // se déclenche en asynchrone sur l'iframe ; sans guard, setData() lève un TypeError sur
  // l'instance détruite et Vite affiche son overlay bloquant tout l'écran.
  useEffect(() => {
    if (mc !== 131 || isRead || !editorContainerRef.current) return
    if (!window.CKEDITOR) { console.warn('[ChampField] window.CKEDITOR non défini'); return }
    let active = true
    const inst = window.CKEDITOR.replace(editorContainerRef.current, fixedHeight ? { height: toCssLength(fixedHeight) } : undefined)
    inst.on('instanceReady', () => {
      if (!active) return
      inst.setData(localRef.current ?? '')
      inst.on('change', () => {
        if (!active) return
        const v = inst.getData()
        skipNextSyncRef.current = true
        localRef.current = v
        setLocal(v)
        // CKEditor ne resynchronise le <textarea> d'origine (name={id}, soumis en FormData) que sur
        // l'événement DOM 'submit' (config.autoUpdateElement) — or tbsGlobalForm.submit() appelle
        // HTMLFormElement.submit() directement, qui NE DÉCLENCHE PAS cet événement. Sans cette écriture
        // imperative à chaque frappe, le <textarea> resterait figé sur sa valeur initiale (defaultValue)
        // lors de la soumission.
        if (editorContainerRef.current) editorContainerRef.current.value = v
        onEventRef.current(id, 'onChange', v)
      })
    })
    editorInstanceRef.current = inst
    return () => { active = false; try { inst.destroy(true) } catch { /* ignore */ } ; editorInstanceRef.current = null }
  }, [mc, isRead]) // eslint-disable-line react-hooks/exhaustive-deps

  // Quill — initialisation sur un div interne créé dynamiquement
  // (évite le double-toolbar en StrictMode : le cleanup vide le wrapper, pas de sibling à traquer)
  useEffect(() => {
    if (mc !== 132 || isRead || !editorContainerRef.current) return
    if (!window.Quill) { console.warn('[ChampField] window.Quill non défini'); return }
    let active = true
    const wrapper = editorContainerRef.current
    wrapper.innerHTML = ''
    const target = document.createElement('div')
    wrapper.appendChild(target)
    const editor = new window.Quill(target, {
      theme: 'snow',
      modules: { toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'clean'],
      ]},
    })
    editor.clipboard.dangerouslyPasteHTML(localRef.current ?? '')
    // `resize: vertical` (jamais horizontal, la largeur suit le conteneur) — nécessite `overflow`
    // différent de `visible` pour que la poignée de redimensionnement native s'affiche.
    editor.root.style.resize = 'vertical'
    editor.root.style.overflowY = 'auto'
    if (fixedHeight) editor.root.style.height = toCssLength(fixedHeight)
    editor.on('text-change', () => {
      if (!active) return
      const v = editor.root.innerHTML
      skipNextSyncRef.current = true
      localRef.current = v
      flushSync(() => setLocal(v))
      onEventRef.current(id, 'onChange', v)
    })
    editorInstanceRef.current = editor
    return () => {
      active = false
      wrapper.innerHTML = ''
      editorInstanceRef.current = null
    }
  }, [mc, isRead]) // eslint-disable-line react-hooks/exhaustive-deps

  // HTML (mc=107) — injection façon jQuery $.fn.html() (setHtmlLikeJQuery), scripts embarqués
  // exécutés à chaque changement de `local` (valeur JSON initiale ou setValue() ultérieur).
  useEffect(() => {
    if (mc !== 107) return
    setHtmlLikeJQuery(htmlContainerRef.current, local)
  }, [mc, local])

  // Sync setValue externe → ACE / CKEditor / Quill — ignoré si `local` vient de l'éditeur
  // lui-même (skipNextSyncRef), sinon chaque frappe rappellerait setValue()/setData()/
  // dangerouslyPasteHTML() et replacerait le curseur en tête de document.
  useLayoutEffect(() => {
    if (!editorInstanceRef.current) return
    if (skipNextSyncRef.current) { skipNextSyncRef.current = false; return }
    if (mc === 130) editorInstanceRef.current.setValue(local ?? '', -1)
    if (mc === 131) {
      editorInstanceRef.current.setData(local ?? '')
      // setData() ne déclenche pas forcément 'change' — écriture directe pour couvrir aussi le cas
      // setValue() externe (pas seulement la frappe utilisateur, cf. handler 'change' ci-dessus).
      if (editorContainerRef.current) editorContainerRef.current.value = local ?? ''
    }
    if (mc === 132) editorInstanceRef.current.clipboard.dangerouslyPasteHTML(local ?? '')
  }, [local, mc])

  if (prevValue !== value) {
    setPrevValue(value)
    setLocal(coerceByMc(mc, value))
  }

  const maskError    = touched && maskState && !testMask(local, maskState)
  // flushSync avant onEvent — mc 104/106 (Date/Heure) portent un input caché compagnon (${id}-val)
  // séparé de l'input visible (formats différents, ISO vs FR/minutes) : sans flush synchrone, du
  // code métier lisant sa valeur DOM de façon synchrone pendant 'onChange' verrait encore l'ancienne
  // valeur (même raison que ChampField.setValue / ComboField). Sans effet sur les types sans
  // compagnon séparé (109/113/116/120/111/112) — l'input visible porte déjà `name` et sa valeur DOM
  // est déjà à jour nativement (saisie navigateur) avant même l'appel à `handleChange`.
  const handleChange = useCallback(val => {
    localRef.current = val
    flushSync(() => setLocal(val))
    setTouched(true)
    setFmtError(null)
    if (requiredState && val !== '' && val != null) setError(false)
    onEvent(id, 'onChange', val)
  }, [id, onEvent, requiredState])
  const handleFocus = useCallback(val => {
    onEvent(id, 'onFocus', val)
  }, [id, onEvent])

  const handleBlur = useCallback(val => {
    setTouched(true)
    if (requiredState && !isRead && mc !== 101 && mc !== 107 && mc !== 119) {
      setError(mc === 115 ? !val : (val === '' || val == null))
    }
    onEvent(id, 'onBlur', val)
  }, [id, onEvent, requiredState, isRead, mc])

  const handleKeyDown = useCallback(e => {
    // `preventDefault` bloque l'action par défaut du navigateur pour Entrée dans un champ texte —
    // la soumission implicite du <form> ancêtre (tbsGenericComponentFrm côté JSP) — qui se
    // déclencherait sinon en plus du traitement métier de 'onEnterKeyPress' (lequel peut lui-même,
    // volontairement, déclencher un submit explicite ailleurs), causant un double submit serveur.
    if (e.key === 'Enter') { e.preventDefault(); onEvent(id, 'onEnterKeyPress') }
  }, [id, onEvent])

  const handleClear = useCallback(() => {
    const emptyVal = [104, 106, 109, 113].includes(mc) ? null : ''
    localRef.current = emptyVal
    flushSync(() => setLocal(emptyVal))
    setError(false)
    setFmtError(null)
    setTouched(false)
    onEvent(id, 'onChange', emptyVal)
  }, [id, onEvent, mc])

  const asterisk = requiredState && !isRead
    ? <span className="ml-0.5 text-[hsl(var(--destructive))]">*</span>
    : null

  // — Button — (pas de required ; `mode: "read"`/`setReadOnly()` désactive le bouton, comme `setDisabled()`)
  if (mc === 101) {
    return (
      <div className="mb-2" data-tbs-root={id} title={tooltip} style={visibilityStyle}>
        {/* `class`/`style` posés directement sur le `<Button>` (fieldElRef), pas sur le wrapper —
            même raison que la surcharge setWidth()/setHeight() ci-dessus : le bouton (inline-flex,
            dimensionné à son contenu) ne s'étire pas pour suivre la taille du wrapper, donc toute
            propriété de mise en forme visuelle (couleur, fond, dimensions...) posée sur le wrapper
            resterait invisible — seul un fond/bordure y serait perceptible (autour du bouton, pas
            dessus), ce qui n'est pas ce qu'on attend de `style` sur un champ bouton. */}
        <Button id={id} ref={fieldElRef} type="button" disabled={disabledState || isRead} onClick={() => onEvent(id, 'onClick')} className={cssClass} style={cssStyle}>{label}</Button>
      </div>
    )
  }

  // — Checkbox —
  if (mc === 115) {
    return (
      <div className={cn('mb-2', cssClass)} data-tbs-root={id} title={tooltip} style={{ ...cssStyle, ...visibilityStyle }}>
        <div className="flex items-center gap-2">
          {/* <input type="hidden"> n'a pas de propriété `checked` native (toujours `false`, quel
              que soit l'attribut posé dessus) : ce <input type="checkbox"> invisible porte donc
              l'id canonique du champ, pour que document.getElementById(id).checked (code métier
              externe, tests, debugger) reflète correctement l'état. `readOnly` : contrôlé, pas
              interactif (l'utilisateur agit sur le <Checkbox> Radix ci-dessous). */}
          <input
            ref={checkboxDomRef}
            id={id}
            type="checkbox"
            checked={!!local}
            readOnly
            tabIndex={-1}
            aria-hidden="true"
            style={{ display: 'none' }}
          />
          {/* Input caché compagnon pour FormData — toujours présent avec "true"/"false", y compris
              décoché (contrairement à un <input type="checkbox"> natif, qui ne soumet rien si
              décoché). */}
          <input id={`${id}-val`} type="hidden" name={id} value={local ? 'true' : 'false'} />
          <Checkbox
            ref={el => { checkboxBtnRef.current = el; fieldElRef.current = el }}
            checked={!!local}
            disabled={isRead || disabledState}
            // onClick sur le <button role="checkbox"> Radix (props transmises telles quelles) :
            // se déclenche aussi bien au clic qu'à la touche ESPACE quand le composant a le focus
            // (comportement natif du <button>), conformément à EVENTS.md (maskComp 101 et 115).
            // Radix appelle ce onClick AVANT son propre onCheckedChange (qui met à jour le state
            // React) : sans mise à jour imperative de checkboxDomRef.checked ici, du code métier
            // lisant .checked de façon synchrone pendant le traitement de onClick verrait encore
            // l'ancienne valeur (le rendu contrôlé de React ne committe qu'après ce handler).
            // Idem pour onCheckedChange ci-dessous vis-à-vis de onChange.
            onClick={isRead || disabledState ? undefined : () => {
              if (checkboxDomRef.current) checkboxDomRef.current.checked = !local
              onEvent(id, 'onClick')
            }}
            onCheckedChange={isRead || disabledState ? undefined : v => {
              if (checkboxDomRef.current) checkboxDomRef.current.checked = v
              localRef.current = v; flushSync(() => setLocal(v)); setError(false)
              onEvent(id, 'onChange', v)
            }}
          />
          {label && (
            <Label
              onClick={isRead || disabledState ? undefined : () => {
                const v = !local
                if (checkboxDomRef.current) checkboxDomRef.current.checked = v
                onEvent(id, 'onClick')
                localRef.current = v; flushSync(() => setLocal(v)); setError(false)
                onEvent(id, 'onChange', v)
              }}
              className={cn('text-sm font-normal', !isRead && 'cursor-pointer')}
            >
              {label}{asterisk}
            </Label>
          )}
        </div>
        {error && <p className="text-xs text-[hsl(var(--destructive))] mt-1">{t('requiredField')}</p>}
      </div>
    )
  }

  // — HTML display — (toujours lecture, pas de required). Contenu injecté imperativement via
  // setHtmlLikeJQuery() (effet ci-dessus), pas dangerouslySetInnerHTML — nécessaire pour exécuter
  // les <script> éventuellement embarqués dans `local`, comme le ferait $.fn.html() de jQuery.
  if (mc === 107) {
    return (
      <div
        ref={el => { htmlContainerRef.current = el; fieldElRef.current = el }}
        className={cn('mb-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--readonly))] px-3 py-2 text-sm max-w-2xl', cssClass)}
        id={id}
        data-tbs-root={id}
        title={tooltip}
        style={{ ...cssStyle, ...visibilityStyle, ...heightStyle }}
      />
    )
  }

  // — ACE editor —
  if (mc === 130) {
    if (isRead) return (
      <div className={cn('mb-2 flex flex-col gap-1', cssClass)} id={id} data-tbs-root={id} title={tooltip} style={{ ...cssStyle, ...visibilityStyle }}>
        {label && <Label>{label}</Label>}
        <input id={`${id}-val`} type="hidden" name={id} value={local ?? ''} />
        <pre ref={fieldElRef} className="px-3 py-2 text-sm rounded-md border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--readonly))] overflow-auto max-h-[300px] resize-y" style={heightStyle}>{local}</pre>
      </div>
    )
    return (
      <div className={cn('mb-2 flex flex-col gap-1', cssClass)} id={id} data-tbs-root={id} title={tooltip} style={{ ...cssStyle, ...visibilityStyle }}>
        {label && <Label>{label}{asterisk}</Label>}
        <input id={`${id}-val`} type="hidden" name={id} value={local ?? ''} />
        {/* overflow: hidden (pas 'auto') — ACE dessine ses propres scrollbars internes en absolute inset:0 ;
            'auto' faisait apparaître une 2e scrollbar native sous Chrome (écart d'arrondi de scrollHeight
            absent sous Firefox). 'hidden' satisfait quand même la contrainte CSS resize (overflow != visible). */}
        <div
          ref={el => { editorContainerRef.current = el; fieldElRef.current = el }}
          style={{ height: fixedHeight ? toCssLength(fixedHeight) : '200px', width: '100%', border: '1px solid hsl(var(--border))', borderRadius: '0.1rem', resize: 'vertical', overflow: 'hidden', ...(disabledState && { opacity: 0.6, pointerEvents: 'none' }) }}
        />
        {error && <p className="text-xs text-[hsl(var(--destructive))]">{t('requiredField')}</p>}
      </div>
    )
  }

  // — CKEditor —
  if (mc === 131) {
    if (isRead) return (
      <div className={cn('mb-2 flex flex-col gap-1', cssClass)} id={id} data-tbs-root={id} title={tooltip} style={{ ...cssStyle, ...visibilityStyle }}>
        {label && <Label>{label}</Label>}
        <input id={`${id}-val`} type="hidden" name={id} value={local ?? ''} />
        <div ref={fieldElRef} className="px-3 py-2 text-sm rounded-md border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--readonly))] resize-y overflow-auto"
          style={heightStyle}
          dangerouslySetInnerHTML={{ __html: local }} />
      </div>
    )
    return (
      <div className={cn('mb-2 flex flex-col gap-1', cssClass)} id={id} data-tbs-root={id} title={tooltip} style={{ ...cssStyle, ...visibilityStyle }}>
        {label && <Label>{label}{asterisk}</Label>}
        <textarea ref={editorContainerRef} name={id} defaultValue={local} className="hidden" style={disabledState ? { pointerEvents: 'none', opacity: 0.6 } : undefined} />
        {error && <p className="text-xs text-[hsl(var(--destructive))]">{t('requiredField')}</p>}
      </div>
    )
  }

  // — Simple éditeur HTML (Quill) —
  if (mc === 132) {
    if (isRead) return (
      <div className={cn('mb-2 flex flex-col gap-1', cssClass)} id={id} data-tbs-root={id} title={tooltip} style={{ ...cssStyle, ...visibilityStyle }}>
        {label && <Label>{label}</Label>}
        <input id={`${id}-val`} type="hidden" name={id} value={local ?? ''} />
        <div ref={fieldElRef} className="px-3 py-2 text-sm rounded-md border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--readonly))] min-h-[80px] resize-y overflow-auto"
          style={heightStyle}
          dangerouslySetInnerHTML={{ __html: local }} />
      </div>
    )
    return (
      <div className={cn('mb-2 flex flex-col gap-1', cssClass)} id={id} data-tbs-root={id} title={tooltip} style={{ ...cssStyle, ...visibilityStyle }}>
        {label && <Label>{label}{asterisk}</Label>}
        <input id={`${id}-val`} type="hidden" name={id} value={local ?? ''} />
        <div ref={el => { editorContainerRef.current = el; fieldElRef.current = el }} style={disabledState ? { pointerEvents: 'none', opacity: 0.6 } : undefined} />
        {error && <p className="text-xs text-[hsl(var(--destructive))]">{t('requiredField')}</p>}
      </div>
    )
  }

  // — Lecture seule pour tous les autres types —
  if (isRead) {
    const isNarrow    = [104, 106].includes(mc)
    const isMultiline = mc === 111 || mc === 112
    const display     = mc === 120
      ? (local ? (showPwd ? local : '••••••••') : '—')
      : mc === 104
        ? (formatFrDate(local) ?? '—')
        : mc === 106
          ? (local != null ? minutesToTimeInput(local) : '—')
          : mc === 109
            ? (local != null ? fmtChampMontant(local) : '—')
            : (local != null && local !== '' ? String(local) : '—')
    return (
      <div className={cn('mb-2 flex flex-col gap-1', cssClass)} data-tbs-root={id} title={tooltip} style={{ ...cssStyle, ...visibilityStyle }}>
        {label && <Label>{label}</Label>}
        <input id={id} type="hidden" name={id} value={champFieldToJsonStr(mc, local) ?? ''} />
        <div ref={fieldElRef} className={cn(
          'px-3 py-2 text-sm rounded-md border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--readonly))] text-[hsl(var(--foreground))]',
          isNarrow ? 'max-w-[180px]' : isMultiline ? 'min-h-[80px] whitespace-pre-wrap resize-y overflow-auto' : mc !== 116 && 'max-w-sm',
          mc === 120 && 'flex items-center gap-2'
        )} style={isMultiline ? heightStyle : undefined}>
          {display}
          {mc === 120 && !!local && (
            <button type="button" tabIndex={-1} onClick={() => setShowPwd(v => !v)}
              className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">
              {showPwd ? <EyeOff size={12} /> : <Eye size={12} />}
            </button>
          )}
        </div>
      </div>
    )
  }

  // — Textarea —
  if (mc === 111 || mc === 112) {
    const hasErr   = maskError || error
    const showClear = !!local && !disabledState
    return (
      <div className={cn('mb-2 flex flex-col gap-1', cssClass)} data-tbs-root={id} title={tooltip} style={{ ...cssStyle, ...visibilityStyle }}>
        {label && <Label htmlFor={id}>{label}{asterisk}</Label>}
        <div className="relative">
          <Textarea
            id={id}
            ref={fieldElRef}
            name={id}
            value={local}
            placeholder={placeholderState}
            disabled={disabledState}
            onFocus={e => handleFocus(e.target.value)}
            onChange={e => handleChange(e.target.value)}
            onBlur={e => handleBlur(e.target.value)}
            className={cn('pr-8', hasErr && 'border-[hsl(var(--destructive))] focus-visible:ring-[hsl(var(--destructive))]', 'resize-y')}
            style={fixedHeight ? heightStyle : undefined}
          />
          {showClear && (
            <button type="button" tabIndex={-1} onClick={handleClear}
              className="absolute right-2 top-2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">
              <X size={12} />
            </button>
          )}
        </div>
        {maskError && <p className="text-xs text-[hsl(var(--destructive))]">{t('invalidFormat')}</p>}
        {error && !maskError && <p className="text-xs text-[hsl(var(--destructive))]">{t('requiredField')}</p>}
      </div>
    )
  }

  // — File — `multiple: true` (JSON) → sélection de plusieurs fichiers dans la boîte de dialogue
  // native. `onChange` émet déjà `e.target.files` (FileList) quel que soit `multiple` — sans cet
  // attribut, le navigateur limite simplement la sélection à un seul fichier.
  if (mc === 119) {
    return (
      <div className={cn('mb-2 flex flex-col gap-1', cssClass)} data-tbs-root={id} title={tooltip} style={{ ...cssStyle, ...visibilityStyle }}>
        {label && <Label htmlFor={id}>{label}{asterisk}</Label>}
        <Input id={id} ref={fieldElRef} name={id} type="file" multiple={!!multiple} className="cursor-pointer max-w-sm" disabled={disabledState}
          onChange={e => onEvent(id, 'onChange', e.target.files)} />
      </div>
    )
  }

  // — Date (mc=104) — `local` est un objet Date JS (coerceByMc/coerceDate), converti en "yyyy-mm-dd"
  // pour l'<input type="date"> HTML (dateToIsoInput/isoInputToDate).
  if (mc === 104) {
    const hasErr    = maskError || error || !!fmtError
    const showClear = local != null && !disabledState
    // showPicker() : API standard (Chrome/Edge 99+, Firefox 101+) pour ouvrir le picker natif
    // depuis un déclencheur quelconque — nécessaire ici puisque sous Chrome l'icône native est
    // masquée (CSS `.tbs-date-input`) et remplacée par ce bouton. try/catch : showPicker() lève
    // (ex. NotAllowedError) si le champ est désactivé ou hors du DOM au moment du clic — fallback
    // focus() plutôt que de laisser l'exception remonter.
    const openDatePicker = () => {
      try { fieldElRef.current?.showPicker?.() }
      catch { fieldElRef.current?.focus() }
    }
    return (
      // maxWidth:'100%' (après cssStyle, donc prioritaire sur un éventuel `style.width` JSON trop
      // large) — testé Chrome/Firefox (Playwright) : les deux respectent correctement `max-width`
      // sur `<input type="date">`, aucun `overflow-x-hidden` supplémentaire n'est nécessaire pour
      // empêcher un débordement sur la cellule voisine. Ne PAS en ajouter ici : posé sur un ancêtre,
      // il masque l'icône calendrier native sous Chrome même quand le champ tient largement dans sa
      // largeur (confirmé par test), sans rapport avec un dépassement réel.
      <div className={cn('mb-2 flex flex-col gap-1', cssClass)} data-tbs-root={id} title={tooltip} style={{ ...cssStyle, maxWidth: '100%', ...visibilityStyle }}>
        {label && <Label htmlFor={id}>{label}{asterisk}</Label>}
        <input id={`${id}-val`} type="hidden" name={id} value={formatFrDate(local) ?? ''} />
        {/* self-start : sans lui, align-items:stretch (flex-col parent) étire ce wrapper à la largeur
            de la cellule/colonne, éloignant le bouton de réinitialisation (absolute) du champ.
            max-w-full (ici et sur l'Input) : un `<input>` garde sinon sa largeur intrinsèque même
            quand son conteneur (cellule de grille étroite) est plus étroit — contrairement à une
            `<div>`, un contrôle de formulaire ne "rétrécit" pas sous sa taille naturelle par défaut
            (shrink-to-fit ignoré), débordant alors sur la cellule voisine. */}
        <div className="relative inline-block self-start max-w-full">
          <Input
            id={id}
            ref={fieldElRef}
            type="date"
            value={dateToIsoInput(local)}
            placeholder={placeholderState}
            onFocus={e => handleFocus(isoInputToDate(e.target.value))}
            onChange={e => handleChange(isoInputToDate(e.target.value))}
            onBlur={e => handleBlur(isoInputToDate(e.target.value))}
            disabled={disabledState}
            onKeyDown={handleKeyDown}
            // px-2 (plutôt que le px-3 par défaut de Input), + pr réservé pour le(s) bouton(s)
            // overlay (calendrier sous Chrome — toujours réservé puisque toujours affiché, voir
            // HAS_WEBKIT_DATE_ICON — et/ou réinitialisation) : même motif que les autres types de
            // champ (overlay absolute à l'intérieur de l'input, pas de sibling externe). Sous
            // Firefox, l'icône native (non masquable en CSS, voir HAS_WEBKIT_DATE_ICON) se positionne
            // elle-même contre le bord de `padding-right` (vérifié par test) : pr-[15px] quand le
            // trigger de réinitialisation est affiché la rapproche donc à 15px du bord, au lieu de
            // pr-7 (28px) qui la laissait trop loin du bouton de réinitialisation.
            className={cn(
              'w-auto max-w-full px-2 tbs-date-input',
              HAS_WEBKIT_DATE_ICON ? (showClear ? 'pr-12' : 'pr-7') : (showClear && 'pr-[15px]'),
              hasErr && 'border-[hsl(var(--destructive))] focus-visible:ring-[hsl(var(--destructive))]'
            )}
          />
          {/* calendrier avant (plus près du dernier chiffre), réinitialisation après (plus à
              droite) — le trigger de réinitialisation "suit" le trigger de date, pas l'inverse. */}
          {HAS_WEBKIT_DATE_ICON && (
            <button type="button" tabIndex={-1} onClick={openDatePicker}
              className={cn(
                'absolute top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]',
                showClear ? 'right-[15px]' : 'right-1.5'
              )}>
              <Calendar size={12} />
            </button>
          )}
          {showClear && (
            <button type="button" tabIndex={-1} onClick={handleClear}
              className={cn(
                'absolute right-1.5 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'
              )}>
              <X size={12} />
            </button>
          )}
        </div>
        {fmtError && <p className="text-xs text-[hsl(var(--destructive))]">{fmtError}</p>}
        {maskError && !fmtError && <p className="text-xs text-[hsl(var(--destructive))]">{t('invalidFormat')}</p>}
        {error && !maskError && !fmtError && <p className="text-xs text-[hsl(var(--destructive))]">{t('requiredField')}</p>}
      </div>
    )
  }

  // — Heure (mc=106) — `local` est un entier (minutes depuis minuit, coerceByMc/coerceInt), converti
  // en "HH:MM" pour l'<input type="time"> HTML (minutesToTimeInput/timeInputToMinutes).
  if (mc === 106) {
    const hasErr    = maskError || error || !!fmtError
    const showClear = local != null && !disabledState
    return (
      <div className={cn('mb-2 flex flex-col gap-1', cssClass)} data-tbs-root={id} title={tooltip} style={{ ...cssStyle, ...visibilityStyle }}>
        {label && <Label htmlFor={id}>{label}{asterisk}</Label>}
        <input id={`${id}-val`} type="hidden" name={id} value={champFieldToJsonStr(mc, local) ?? ''} />
        <div className="relative max-w-[180px]">
          <Input
            id={id}
            ref={fieldElRef}
            type="time"
            value={minutesToTimeInput(local)}
            placeholder={placeholderState}
            onFocus={e => handleFocus(timeInputToMinutes(e.target.value))}
            onChange={e => handleChange(timeInputToMinutes(e.target.value))}
            onBlur={e => handleBlur(timeInputToMinutes(e.target.value))}
            disabled={disabledState}
            onKeyDown={handleKeyDown}
            className={cn('w-full', showClear && 'pr-7', hasErr && 'border-[hsl(var(--destructive))] focus-visible:ring-[hsl(var(--destructive))]')}
          />
          {showClear && (
            <button type="button" tabIndex={-1} onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">
              <X size={12} />
            </button>
          )}
        </div>
        {fmtError && <p className="text-xs text-[hsl(var(--destructive))]">{fmtError}</p>}
        {maskError && !fmtError && <p className="text-xs text-[hsl(var(--destructive))]">{t('invalidFormat')}</p>}
        {error && !maskError && !fmtError && <p className="text-xs text-[hsl(var(--destructive))]">{t('requiredField')}</p>}
      </div>
    )
  }

  // — Tous les autres inputs —
  const typeMap = {
    109: 'number',
    113: 'number',
    116: 'text',
    120: 'password',
  }
  const isPwd     = mc === 120
  const isNum     = mc === 109 || mc === 113
  const inputType = isPwd ? (showPwd ? 'text' : 'password') : (typeMap[mc] ?? 'text')
  const hasErr    = maskError || error || !!fmtError
  const showClear = local !== '' && local != null && !disabledState
  // Numerique/Montant : `local` est un flottant JS (coerceByMc/coerceFloat) — l'<input type="number">
  // natif accepte directement un nombre (ou '' pour vide) en `value`, mais `e.target.value` reste
  // toujours une chaîne côté événements : conversion à chaque call site plutôt que dans handleChange
  // (partagée avec 116/120, qui restent des chaînes).

  return (
    <div className={cn('mb-2 flex flex-col gap-1', cssClass)} data-tbs-root={id} title={tooltip} style={{ ...cssStyle, ...visibilityStyle }}>
      {label && <Label htmlFor={id}>{label}{asterisk}</Label>}
      <div className={cn('relative', mc !== 116 && 'max-w-sm')}>
        <Input
          id={id}
          ref={fieldElRef}
          name={id}
          type={inputType}
          value={local ?? ''}
          placeholder={placeholderState}
          disabled={disabledState}
          // Empêche l'autofill du gestionnaire de mots de passe du navigateur : un champ texte
          // suivi d'un champ password (mc=120) est détecté par heuristique comme un formulaire de
          // connexion (peu importe le nom des champs), et déclenche onChange sur les deux champs
          // au chargement, sans aucun setValue() applicatif. "new-password" (plutôt que "off",
          // ignoré par Chrome pour les champs password) indique qu'il ne s'agit pas d'un login existant.
          autoComplete={isPwd ? 'new-password' : 'off'}
          onFocus={e => handleFocus(isNum ? coerceFloat(e.target.value) : e.target.value)}
          onChange={e => handleChange(isNum ? coerceFloat(e.target.value) : e.target.value)}
          onBlur={e => handleBlur(isNum ? coerceFloat(e.target.value) : e.target.value)}
          onKeyDown={handleKeyDown}
          className={cn(
            'w-full',
            (showClear || isPwd) && (showClear && isPwd ? 'pr-14' : 'pr-7'),
            hasErr && 'border-[hsl(var(--destructive))] focus-visible:ring-[hsl(var(--destructive))]'
          )}
        />
        {isPwd && (
          <button type="button" tabIndex={-1} onClick={() => setShowPwd(v => !v)}
            className={cn(
              'absolute top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]',
              showClear ? 'right-8' : 'right-2'
            )}>
            {showPwd ? <EyeOff size={12} /> : <Eye size={12} />}
          </button>
        )}
        {showClear && (
          <button type="button" tabIndex={-1} onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">
            <X size={12} />
          </button>
        )}
      </div>
      {fmtError && <p className="text-xs text-[hsl(var(--destructive))]">{fmtError}</p>}
      {maskError && !fmtError && <p className="text-xs text-[hsl(var(--destructive))]">{t('invalidFormat')}</p>}
      {error && !maskError && !fmtError && <p className="text-xs text-[hsl(var(--destructive))]">{t('requiredField')}</p>}
    </div>
  )
}
